/**
 * MAESTRO Preis-Engine (Spec 02, D "Engine") — deterministisch, ausschliesslich Integer-Cents.
 *
 * EINE Quelle der Wahrheit fuer UI-Vorschau, Public-Offer, LexOffice-Export und Stripe. Der
 * Neubau ersetzt die drei parallel rechnenden Alt-Implementierungen (Client/Anzeige/Edge) und
 * die dortigen Geld-Bugs (Float-Euro, Faktor-N bei per_event, rueckwaerts geratene USt).
 *
 * Leitplanken:
 *  - Geld immer Integer-Cent, Brutto. Mengen als Tausendstel (qty_milli), Division erst NACH
 *    der Multiplikation, Rundung pro Zeile auf Cent — kein IEEE-754 im Geldpfad.
 *  - USt je Satz aus Brutto: vat = gross - round(gross / (1 + rate)). Exakt dieselbe Formel
 *    im LexOffice-Export.
 *  - Rabatt/Anpassung je USt-Bucket per Largest-Remainder verteilt (cent-exakt, deterministisch:
 *    groesster Restanteil zuerst, dann rate_bp aufsteigend).
 */

export type PriceMode = "per_person" | "per_unit" | "flat";

/** Sektions-Konstante fuer die (max. eine) Preisanpassungs-Zeile je Option. */
export const ADJUSTMENT_SECTION = "adjustment";

export interface EngineItem {
  id?: string;
  section: string; // 'menu'|'drinks'|'equipment'|'staff'|'other'|'adjustment'
  priceMode: PriceMode;
  /** Menge in Tausendstel (1000 = 1, 330 = 0,33). */
  qtyMilli: number;
  /** Brutto-Einzelpreis in Cent. Bei der Anpassungszeile ggf. negativ. */
  unitPriceCents: number;
  /** MwSt-Satz in Basispunkten (1900 = 19 %, 700 = 7 %, 0 = steuerfrei/§19). */
  vatRateBp: number;
  discountable?: boolean; // default true
  /**
   * H-21: optionale Position ("auf Wunsch zubuchbar"). Wird in lineTotals ausgewiesen
   * (Anzeige-Preis), zaehlt aber NICHT in Zwischensumme, Rabattbasis, USt-Buckets oder
   * Endpreis — der Kunde darf nicht fuer etwas zahlen, das als optional ausgewiesen ist.
   */
  isOptional?: boolean;
  /** Tagesbezug fuer per_person; null/undefined => Gaestezahl der Option. */
  dayNo?: number | null;
}

export interface EngineInput {
  items: EngineItem[];
  /** Kopf-Gaestezahl der Option (Fallback fuer per_person ohne Tagesbezug). */
  optionGuestCount: number;
  /** dayNo -> Gaestezahl (aus offer_option_days). */
  guestCountByDay?: Record<number, number>;
  /** Rabatt in Basispunkten (2500 = 25 %). XOR zu discountAmountCents. */
  discountPercentBp?: number | null;
  /** Rabatt als fester Betrag in Cent. XOR zu discountPercentBp. */
  discountAmountCents?: number | null;
}

export interface VatBucket {
  rateBp: number;
  netCents: number;
  vatCents: number;
  grossCents: number;
}

export interface LineTotal {
  id?: string;
  grossCents: number;
}

export interface PriceBreakdown {
  subtotalCents: number; // Summe der Nicht-Anpassungs-Zeilen (brutto)
  discountCents: number; // effektiv gewaehrter Rabatt (>= 0)
  adjustmentCents: number; // Summe der Anpassungs-Zeilen (kann negativ sein)
  amountTotalCents: number; // subtotal - discount + adjustment
  perPersonCents: number; // amountTotal / optionGuestCount (0 bei 0 Gaesten)
  vatBreakdown: VatBucket[]; // je Satz, aufsteigend nach rateBp
  lineTotals: LineTotal[];
}

/** Gaestezahl fuer eine Zeile: Tagesbezug schlaegt Options-Default. */
function guestsFor(item: EngineItem, input: EngineInput): number {
  if (item.dayNo != null && input.guestCountByDay && item.dayNo in input.guestCountByDay) {
    return input.guestCountByDay[item.dayNo]!;
  }
  return input.optionGuestCount;
}

/** Brutto-Zeilensumme in Cent. Division durch 1000 erst nach der Multiplikation, dann runden. */
export function lineGross(item: EngineItem, guests: number): number {
  switch (item.priceMode) {
    case "per_person":
      return Math.round((item.qtyMilli * item.unitPriceCents * guests) / 1000);
    case "per_unit":
      return Math.round((item.qtyMilli * item.unitPriceCents) / 1000);
    case "flat":
      return item.unitPriceCents;
  }
}

/** USt aus Brutto fuer einen Satz (bp). rate 0 => 0. */
export function vatFromGross(grossCents: number, rateBp: number): number {
  if (rateBp <= 0) return 0;
  const net = Math.round((grossCents * 10000) / (10000 + rateBp));
  return grossCents - net;
}

/**
 * Verteilt `amount` (>= 0) proportional zu `weights` auf ganze Cent per Largest-Remainder.
 * Rueckgabe hat dieselbe Laenge wie weights und summiert sich EXAKT zu `amount`.
 * Tie-Break: groesserer Restanteil zuerst, dann kleinerer tieKey (rate_bp) — deterministisch.
 */
export function largestRemainder(amount: number, weights: number[], tieKeys: number[]): number[] {
  const total = weights.reduce((s, w) => s + w, 0);
  const out = weights.map(() => 0);
  if (amount <= 0 || total <= 0) return out;

  const rema: { idx: number; frac: number; tie: number }[] = [];
  let assigned = 0;
  weights.forEach((w, i) => {
    const exact = (amount * w) / total;
    const floor = Math.floor(exact);
    out[i] = floor;
    assigned += floor;
    rema.push({ idx: i, frac: exact - floor, tie: tieKeys[i] ?? 0 });
  });
  let rest = amount - assigned;
  rema.sort((a, b) => (b.frac - a.frac) || (a.tie - b.tie));
  for (let k = 0; k < rema.length && rest > 0; k++) {
    const j = rema[k]!.idx;
    out[j] = (out[j] ?? 0) + 1;
    rest--;
  }
  return out;
}

/**
 * Rechnet die vollstaendige Preisaufschluesselung einer Option.
 * Reihenfolge (Spec D): Zwischensumme -> Rabatt (nur discountable, nicht Anpassung)
 * -> Anpassung -> Endpreis. USt je Satz auf den finalen Brutto-Betrag pro Bucket.
 */
export function computeBreakdown(input: EngineInput): PriceBreakdown {
  const lineTotals: LineTotal[] = [];
  let subtotalCents = 0;
  let adjustmentCents = 0;

  // Brutto je Bucket (rate_bp) sowie der discountable Nicht-Anpassungs-Anteil je Bucket.
  const grossByBucket = new Map<number, number>();
  const discountableByBucket = new Map<number, number>();
  let discountBase = 0;

  for (const item of input.items) {
    const gross = lineGross(item, guestsFor(item, input));
    lineTotals.push({ id: item.id, grossCents: gross });
    // H-21: optionale Positionen sind reine Anzeige-Zeilen — kein Beitrag zu Summe/Rabatt/USt.
    if (item.isOptional === true) continue;
    grossByBucket.set(item.vatRateBp, (grossByBucket.get(item.vatRateBp) ?? 0) + gross);

    const isAdjustment = item.section === ADJUSTMENT_SECTION;
    if (isAdjustment) {
      adjustmentCents += gross;
    } else {
      subtotalCents += gross;
      // N-G (Bughunt 2026-07-11): negative Positionen (z. B. Gutschrift-/Abzugszeilen) zaehlen
      // NICHT zur rabattfaehigen Basis. Sonst verzerrt ein negatives Gewicht die proportionale
      // USt-Aufteilung je Satz (Largest-Remainder ist nur fuer Gewichte >= 0 summenexakt).
      const discountable = item.discountable !== false && gross > 0;
      if (discountable) {
        discountBase += gross;
        discountableByBucket.set(item.vatRateBp, (discountableByBucket.get(item.vatRateBp) ?? 0) + gross);
      }
    }
  }

  // Rabatt bestimmen (auf die discountable Basis begrenzt; nie negativ).
  let discountCents = 0;
  if (input.discountPercentBp && input.discountPercentBp > 0) {
    discountCents = Math.round((discountBase * input.discountPercentBp) / 10000);
  } else if (input.discountAmountCents && input.discountAmountCents > 0) {
    discountCents = input.discountAmountCents;
  }
  discountCents = Math.max(0, Math.min(discountCents, discountBase));

  // Rabatt proportional zum discountable Brutto je Bucket verteilen (Largest-Remainder).
  const buckets = [...grossByBucket.keys()].sort((a, b) => a - b);
  // N-G: Gewichte defensiv auf >= 0 klemmen (die Basis oben ist bereits negativ-frei).
  const discWeights = buckets.map((b) => Math.max(0, discountableByBucket.get(b) ?? 0));
  const discShares = largestRemainder(discountCents, discWeights, buckets);

  const vatBreakdown: VatBucket[] = [];
  let totalCents = 0;
  buckets.forEach((rateBp, i) => {
    const grossFinal = (grossByBucket.get(rateBp) ?? 0) - (discShares[i] ?? 0);
    const vatCents = vatFromGross(grossFinal, rateBp);
    vatBreakdown.push({ rateBp, grossCents: grossFinal, vatCents, netCents: grossFinal - vatCents });
    totalCents += grossFinal;
  });

  const perPersonCents = input.optionGuestCount > 0 ? Math.round(totalCents / input.optionGuestCount) : 0;

  return {
    subtotalCents,
    discountCents,
    adjustmentCents,
    amountTotalCents: totalCents,
    perPersonCents,
    vatBreakdown,
    lineTotals,
  };
}

/**
 * Berechnet den Einzelpreis (flat) einer Anpassungszeile, sodass der Endpreis der Option den
 * Zielpreis trifft: adjustment = target - (aktueller Endpreis OHNE bestehende Anpassung).
 * Der Aufrufer legt damit die (max. eine) section='adjustment'-Zeile an/aktualisiert sie.
 */
export function targetPriceAdjustmentCents(input: EngineInput, targetCents: number): number {
  const withoutAdjustment: EngineInput = {
    ...input,
    items: input.items.filter((it) => it.section !== ADJUSTMENT_SECTION),
  };
  const base = computeBreakdown(withoutAdjustment).amountTotalCents;
  return targetCents - base;
}
